import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getHrAdminDashboard } from '../../services/hr_dashboard';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/BreadCrumbs';
import { getUserRole } from '../../helpers/local_storage_helpers';
import DateRangePicker from '../../components/inputFields/DateRangePicker';

/* ═══════════════════════════ Font Tokens ═══════════════════════════ */
const FONT = {
    display: "'Fraunces', Georgia, serif",
    ui: "'Inter', -apple-system, sans-serif",
    mono: "'JetBrains Mono', 'Menlo', monospace",
};

/* ═══════════════════════════ Helpers ═══════════════════════════ */
const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtTime = (val) => {
    if (!val) return '—';
    const s = String(val);
    if (s.includes('T')) {
        const d = new Date(s);
        if (!isNaN(d)) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    const parts = s.split(':');
    if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    return s;
};

const fmtWorkingHours = (val) => {
    if (!val) return '—';
    if (typeof val === 'string' && val.includes(':')) {
        const [h, m] = val.split(':').map(Number);
        if (h <= 0 && m <= 0) return '0h';
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
    }
    if (typeof val === 'number' && isFinite(val)) {
        const h = Math.floor(val);
        const m = Math.round((val - h) * 60);
        return m > 0 ? `${h}h ${m}m` : `${h}h`;
    }
    return String(val);
};

const fmtTalkTime = (secs) => {
    if (!secs || secs === 0) return '0m';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

/* ═══════════════════════════ Shared Styles ═══════════════════════════ */
const COLORS = {
    bg: '#f7f6f3',
    card: '#ffffff',
    border: '#e8e7e4',
    textPrimary: '#1a1a1a',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    accent: '#3d5a3e',
    accentLight: '#e8efe8',
    success: '#22c55e',
    warning: '#f59e0b',
    danger: '#ef4444',
    blue: '#3b82f6',
    orange: '#f97316',
    violet: '#8b5cf6',
};

const AVATAR_COLORS = [
    '#3d5a3e', '#5b4a9e', '#c2703e', '#2d7d9a', '#8b4562',
    '#4a6741', '#7c5c3c', '#3a6b8c', '#6b4c8a', '#8c6d3f',
];
const getAvatarColor = (name) => {
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

/* ═══════════════════════════ Date Quick-select Helpers ═══════════════════════════ */
const getMonday = (d) => {
    const dt = new Date(d);
    const day = dt.getDay();
    const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
    dt.setDate(diff);
    return dt.toISOString().slice(0, 10);
};

const getMonthStart = (d) => {
    const dt = new Date(d);
    return new Date(dt.getFullYear(), dt.getMonth(), 1).toISOString().slice(0, 10);
};

/* ═══════════════════════════ Summary Strip ═══════════════════════════ */
const SummaryStrip = ({ summary, totalFollowUps }) => {
    if (!summary) return null;
    const convPct = summary.conversion_percentage ?? 0;
    const npcPct = summary.unique_leads_assigned > 0
        ? ((summary.npc_count / summary.unique_leads_assigned) * 100).toFixed(1) : 0;

    const cards = [
        {
            label: 'UNIQUE LEADS ASSIGNED',
            value: (summary.unique_leads_assigned ?? 0).toLocaleString(),
            sub: null,
            highlight: true,
        },
        {
            label: 'NEW / UNTOUCHED',
            value: (summary.new_leads_left ?? 0).toLocaleString(),
            sub: summary.new_leads_left === 0 ? '✓ Fully assigned' : `${summary.new_leads_left} remaining`,
            subColor: summary.new_leads_left === 0 ? COLORS.accent : COLORS.warning,
        },
        {
            label: 'NPC',
            value: (summary.npc_count ?? 0).toLocaleString(),
            sub: `${npcPct}% of assigned`,
        },
        {
            label: 'CONVERTED',
            value: (summary.converted_leads ?? 0).toLocaleString(),
            sub: null,
        },
        {
            label: 'CONVERSION RATE',
            value: `${convPct}`,
            valueSuffix: '%',
            sub: null,
            valueColor: convPct >= 5 ? COLORS.success : convPct >= 2 ? COLORS.warning : COLORS.danger,
        },
        {
            label: 'ACTIVE FOLLOW-UPS',
            value: totalFollowUps.toLocaleString(),
            sub: 'Across all reps',
        },
    ];

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            background: COLORS.card,
            borderRadius: 14,
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden',
            marginBottom: 24,
        }}>
            {cards.map((c, i) => (
                <div key={c.label} style={{
                    padding: '18px 20px',
                    borderRight: i < cards.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                    background: c.highlight ? '#f4f8f4' : 'transparent',
                }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.06em', marginBottom: 8 }}>
                        {c.label}
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: c.valueColor || COLORS.textPrimary, lineHeight: 1 }}>
                        {c.value}
                        {c.valueSuffix && <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 1 }}>{c.valueSuffix}</span>}
                    </div>
                    {c.sub && (
                        <div style={{ fontSize: 11, color: c.subColor || COLORS.textMuted, marginTop: 6, fontWeight: 500 }}>
                            {c.sub}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

/* ═══════════════════════════ Clock Box ═══════════════════════════ */
const ClockBox = ({ label, value, hasValue }) => (
    <div style={{
        flex: 1,
        background: '#fafaf8',
        borderRadius: 10,
        padding: '10px 12px',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 64,
    }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
            {label}
        </div>
        <div style={{ fontSize: hasValue ? 18 : 22, fontWeight: 700, color: hasValue ? COLORS.textPrimary : COLORS.textMuted, lineHeight: 1 }}>
            {value}
        </div>
        {/* Decorative mini wave */}
        <svg viewBox="0 0 80 20" style={{ position: 'absolute', bottom: 4, right: 4, width: 50, height: 14, opacity: 0.08 }}>
            <path d="M0 15 Q10 5 20 12 T40 10 T60 13 T80 8" fill="none" stroke={COLORS.textPrimary} strokeWidth="2" />
        </svg>
    </div>
);

/* ═══════════════════════════ Lead Status Bar ═══════════════════════════ */
const LEAD_SEGMENTS = [
    { key: 'new_count', color: '#3b82f6', label: 'NEW' },
    { key: 'callback_count', color: '#f59e0b', label: 'CALLBACK' },
    { key: 'follow_up_count', color: '#f97316', label: 'F/U' },
    { key: 'interested_count', color: '#8b5cf6', label: 'INT.' },
    { key: 'npc_count', color: '#6b7280', label: 'NPC' },
    { key: 'not_interested_count', color: '#ef4444', label: 'N/I' },
    { key: 'converted_count', color: '#22c55e', label: 'CONV.' },
];

const LeadBar = ({ leads }) => {
    const total = leads.total_assigned || 0;
    if (total === 0) return null;

    return (
        <div style={{ height: 6, borderRadius: 3, overflow: 'hidden', display: 'flex', background: '#f0f0ee' }}>
            {LEAD_SEGMENTS.map(seg => {
                const val = leads[seg.key] || 0;
                if (val === 0) return null;
                const pct = (val / total) * 100;
                return (
                    <div key={seg.key} style={{ width: `${pct}%`, background: seg.color, minWidth: pct > 0 ? 3 : 0 }} />
                );
            })}
        </div>
    );
};

/* ═══════════════════════════ Staff Card ═══════════════════════════ */
const StaffCard = ({ staff, showLeads }) => {
    const [expanded, setExpanded] = useState(false);
    const cs = staff.call_stats || {};
    const ld = staff.leads || {};

    const attendanceParts = (staff.attendance || '0/0').split('/');
    const attPresent = Number(attendanceParts[0] || 0);
    const attTotal = Number(attendanceParts[1] || 1);
    const attPct = attTotal > 0 ? Math.round((attPresent / attTotal) * 100) : 0;

    const totalCalls = cs.total_calls || 0;
    const hasLeads = (ld.total_assigned || 0) > 0;
    const convRate = hasLeads ? ((ld.converted_count || 0) / ld.total_assigned * 100).toFixed(1) : '0.0';
    const pendingFU = staff.follow_up_count || 0;
    const isClockedIn = !!cs.clock_in_time && !cs.clock_out_time;

    const avatarBg = getAvatarColor(staff.name);
    const attBadgeBg = attPct >= 80 ? '#ecfdf5' : attPct >= 50 ? '#fffbeb' : '#fef2f2';
    const attBadgeColor = attPct >= 80 ? '#059669' : attPct >= 50 ? '#d97706' : '#dc2626';
    const attBadgeBorder = attPct >= 80 ? '#a7f3d0' : attPct >= 50 ? '#fde68a' : '#fecaca';

    // Main 5 breakdown labels shown below bar
    const breakdownItems = [
        { key: 'new_count', label: 'NEW', color: '#3b82f6' },
        { key: 'callback_count', label: 'CALLBACK', color: '#f59e0b' },
        { key: 'npc_count', label: 'NPC', color: '#6b7280' },
        { key: 'not_interested_count', label: 'N/I', color: '#ef4444' },
        { key: 'converted_count', label: 'CONV.', color: '#22c55e' },
    ];

    // Extra details shown on expand
    const detailItems = [
        { key: 'follow_up_count', label: 'Follow-up', color: '#f97316' },
        { key: 'interested_count', label: 'Interested', color: '#8b5cf6' },
    ];

    return (
        <div style={{
            background: COLORS.card,
            borderRadius: 14,
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden',
            transition: 'box-shadow 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.06)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
        >
            {/* Header */}
            <div style={{ padding: '16px 18px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{
                        width: 38, height: 38, borderRadius: '50%', background: avatarBg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
                    }}>
                        {getInitials(staff.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {staff.name}
                            {isClockedIn && <span style={{ marginLeft: 4, color: COLORS.success }} title="Currently clocked in">●</span>}
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
                            +91 {staff.phone_number || '—'}
                        </div>
                    </div>
                </div>
                <div style={{
                    padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    background: attBadgeBg, color: attBadgeColor, border: `1px solid ${attBadgeBorder}`,
                    textAlign: 'center', lineHeight: 1.2,
                }}>
                    <div>{staff.attendance || '0/0'}</div>
                    <div style={{ fontSize: 9, fontWeight: 600, marginTop: 2, opacity: 0.8 }}>{attPct}% present</div>
                </div>
            </div>

            {/* Clock boxes */}
            <div style={{ padding: '0 18px 14px', display: 'flex', gap: 8 }}>
                <ClockBox label="Clock In" value={fmtTime(cs.clock_in_time)} hasValue={!!cs.clock_in_time} />
                <ClockBox label="Clock Out" value={fmtTime(cs.clock_out_time)} hasValue={!!cs.clock_out_time} />
                <ClockBox label="Hours" value={fmtWorkingHours(cs.working_hours)} hasValue={!!cs.working_hours} />
            </div>

            {/* Leads section */}
            {showLeads && (
                <div style={{ padding: '0 18px 14px' }}>
                    {/* Leads header + bar */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.04em' }}>
                            LEADS · <span style={{ color: COLORS.textPrimary }}>{ld.total_assigned || 0} ASSIGNED</span>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textPrimary }}>
                            {convRate} <span style={{ fontSize: 10, fontWeight: 500, color: COLORS.textMuted }}>conv.</span>
                        </div>
                    </div>

                    <LeadBar leads={ld} />

                    {/* Breakdown dots */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 10 }}>
                        {breakdownItems.map(item => (
                            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, display: 'inline-block' }} />
                                <span style={{ fontSize: 9, fontWeight: 600, color: COLORS.textMuted }}>{item.label}</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textPrimary }}>{ld[item.key] || 0}</span>
                            </div>
                        ))}
                    </div>

                    {/* Expanded details */}
                    {expanded && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${COLORS.border}` }}>
                            {detailItems.map(item => (
                                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, display: 'inline-block' }} />
                                    <span style={{ fontSize: 9, fontWeight: 600, color: COLORS.textMuted }}>{item.label}</span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textPrimary }}>{ld[item.key] || 0}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Bottom stats row */}
            <div style={{
                padding: '10px 18px',
                borderTop: `1px solid ${COLORS.border}`,
                display: 'flex', alignItems: 'center', gap: 16,
                background: '#fafaf8',
            }}>
                <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.04em' }}>FOLLOW-UPS</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: pendingFU > 50 ? COLORS.danger : pendingFU > 0 ? COLORS.orange : COLORS.textPrimary }}>
                        {pendingFU}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.04em' }}>TALK TIME</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{fmtTalkTime(cs.total_talk_time)}</div>
                </div>
                <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.04em' }}>CALLS</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{totalCalls}</div>
                </div>
                <button
                    onClick={() => setExpanded(v => !v)}
                    style={{
                        marginLeft: 'auto', fontSize: 12, fontWeight: 600,
                        color: COLORS.textSecondary, background: 'none', border: `1px solid ${COLORS.border}`,
                        borderRadius: 8, padding: '5px 12px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                        transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.border}
                >
                    Details
                    <svg viewBox="0 0 12 12" style={{ width: 10, height: 10, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                        <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

/* ═══════════════════════════ Today Sidebar Panel ═══════════════════════════ */
const TodayPanel = ({ todays_sales, staff, data }) => {
    const revenue = parseFloat(todays_sales?.total_revenue || 0);
    const orders = todays_sales?.total_orders || 0;
    const clockedIn = (staff || []).filter(s => !!s.call_stats?.clock_in_time).length;
    const totalStaff = (staff || []).length;

    const fmtRevenue = (r) => {
        if (r >= 100000) return `₹${(r / 100000).toFixed(1)}L`;
        if (r >= 1000) return `₹${(r / 1000).toFixed(1)}K`;
        return `₹${r.toFixed(0)}`;
    };

    return (
        <div style={{
            background: COLORS.card,
            borderRadius: 14,
            border: `1px solid ${COLORS.border}`,
            overflow: 'hidden',
            marginBottom: 16,
        }}>
            <div style={{ padding: '16px 18px 12px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>Today</div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    {fmtDate(data?.end_date || todayStr())}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.success, display: 'inline-block' }} />
                        <span style={{ fontWeight: 600, color: COLORS.textSecondary }}>live</span>
                    </span>
                </div>
            </div>

            {/* Revenue / Orders card */}
            <div style={{
                margin: '0 12px 12px',
                background: 'linear-gradient(135deg, #3d5a3e, #4a6f4c)',
                borderRadius: 12,
                padding: '16px 18px',
                color: '#fff',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, letterSpacing: '0.06em' }}>REVENUE</div>
                        <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1, marginTop: 4 }}>{fmtRevenue(revenue)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, letterSpacing: '0.06em' }}>ORDERS</div>
                        <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1, marginTop: 4 }}>{orders}</div>
                    </div>
                </div>
            </div>

            {/* Avg order + clocked in */}
            <div style={{ padding: '0 18px 16px', display: 'flex', gap: 20 }}>
                <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.04em' }}>AVG ORDER</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginTop: 2 }}>
                        ₹{orders > 0 ? Math.round(revenue / orders).toLocaleString() : '0'}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.04em' }}>CLOCKED IN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginTop: 2 }}>
                        {clockedIn} of {totalStaff}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ═══════════════════════════ Leaderboard Panel ═══════════════════════════ */
const LeaderboardPanel = ({ staff }) => {
    const ranked = useMemo(() => {
        return (staff || [])
            .filter(s => (s.leads?.total_assigned || 0) > 0)
            .map(s => {
                const assigned = s.leads.total_assigned;
                const worked = assigned - (s.leads.new_count || 0);
                const pct = Math.round((worked / assigned) * 100);
                return { ...s, worked, pct };
            })
            .sort((a, b) => b.pct - a.pct)
            .slice(0, 4);
    }, [staff]);

    if (ranked.length === 0) return null;

    return (
        <div style={{
            background: COLORS.card,
            borderRadius: 14,
            border: `1px solid ${COLORS.border}`,
            padding: '16px 18px',
            marginBottom: 16,
        }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 2 }}>Top by leads worked</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 14 }}>Assigned minus new</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {ranked.map((s, i) => (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, width: 18, textAlign: 'right' }}>
                            {i < 3 ? `#${i + 1}` : ''}
                        </span>
                        <div style={{
                            width: 30, height: 30, borderRadius: '50%', background: getAvatarColor(s.name),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 700, fontSize: 10, flexShrink: 0,
                        }}>
                            {getInitials(s.name)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {s.name}
                            </div>
                            <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                                {s.worked} of {s.leads.total_assigned} worked
                            </div>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{s.pct}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ═══════════════════════════ Attention Panel ═══════════════════════════ */
const AttentionPanel = ({ staff, summary }) => {
    const alerts = useMemo(() => {
        const items = [];
        const staffList = staff || [];

        // Zero calls today
        const zeroCalls = staffList.filter(s => (s.call_stats?.total_calls || 0) === 0 && (s.leads?.total_assigned || 0) > 0);
        if (zeroCalls.length > 0) {
            items.push({
                type: 'warning',
                text: `${zeroCalls.length} rep${zeroCalls.length !== 1 ? 's have' : ' has'} logged 0 calls today despite assigned leads.`,
            });
        }

        // Highest follow-ups
        const maxFU = staffList.reduce((max, s) => (s.follow_up_count || 0) > (max?.follow_up_count || 0) ? s : max, null);
        if (maxFU && (maxFU.follow_up_count || 0) > 30) {
            const firstName = maxFU.name?.split(/\s+/)[0] || maxFU.name;
            items.push({
                type: 'danger',
                text: `${firstName} has ${maxFU.follow_up_count} open follow-ups — highest on the floor.`,
            });
        }

        // Conversion rate insight
        const convPct = summary?.conversion_percentage ?? 0;
        if (convPct > 0) {
            items.push({
                type: 'success',
                text: `Conversion rate at ${convPct}% for this period.`,
            });
        }

        // Untouched leads
        const untouched = summary?.new_leads_left ?? 0;
        items.push({
            type: 'info',
            text: untouched > 0
                ? `${untouched} untouched leads remaining — consider reassigning.`
                : '0 untouched leads remaining — consider another batch import.',
        });

        return items;
    }, [staff, summary]);

    const typeStyles = {
        warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠', iconColor: '#d97706' },
        danger: { bg: '#fef2f2', border: '#fecaca', icon: '●', iconColor: '#dc2626' },
        success: { bg: '#ecfdf5', border: '#a7f3d0', icon: '●', iconColor: '#059669' },
        info: { bg: '#f0f9ff', border: '#bae6fd', icon: '●', iconColor: '#0284c7' },
    };

    if (alerts.length === 0) return null;

    return (
        <div style={{
            background: COLORS.card,
            borderRadius: 14,
            border: `1px solid ${COLORS.border}`,
            padding: '16px 18px',
        }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 2 }}>Attention</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 14 }}>Auto-flagged this period</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {alerts.map((a, i) => {
                    const st = typeStyles[a.type] || typeStyles.info;
                    return (
                        <div key={i} style={{
                            padding: '10px 12px',
                            borderRadius: 10,
                            background: st.bg,
                            border: `1px solid ${st.border}`,
                            display: 'flex', gap: 8, alignItems: 'flex-start',
                        }}>
                            <span style={{ color: st.iconColor, fontSize: 12, lineHeight: 1.5, flexShrink: 0 }}>{st.icon}</span>
                            <span style={{ fontSize: 12, color: COLORS.textPrimary, lineHeight: 1.5 }}>{a.text}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ═══════════════════════════ MAIN PAGE ═══════════════════════════ */
export default function B2CSalesAdminDashboard() {
    const showLeads = true; // admin-only page — always show leads

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [searchQ, setSearchQ] = useState('');
    const [sortKey, setSortKey] = useState('name');
    const [activeTab, setActiveTab] = useState('today');

    const fetchData = useCallback(async (sd, ed) => {
        setLoading(true);
        try {
            const res = await getHrAdminDashboard(sd, ed);
            if (res.status === 200) setData(res.data);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load HR dashboard data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(null, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        const today = todayStr();
        if (tab === 'today') {
            setDateRange({ start: today, end: today });
            fetchData(today, today);
        } else if (tab === 'week') {
            const monday = getMonday(new Date());
            setDateRange({ start: monday, end: today });
            fetchData(monday, today);
        } else if (tab === 'mtd') {
            const mStart = getMonthStart(new Date());
            setDateRange({ start: mStart, end: today });
            fetchData(mStart, today);
        } else if (tab === 'custom') {
            // just switch to custom mode, user picks from DateRangePicker
        }
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        setActiveTab('custom');
        if (range.start && range.end) fetchData(range.start, range.end);
    };

    const handleResetDate = () => {
        setDateRange({ start: null, end: null });
        setActiveTab('today');
        fetchData(null, null);
    };

    const staffList = data?.staff || [];
    const totalFollowUps = staffList.reduce((sum, s) => sum + (s.follow_up_count || 0), 0);
    const clockedInCount = staffList.filter(s => !!s.call_stats?.clock_in_time).length;

    const filteredSorted = staffList
        .filter(s => {
            if (!searchQ) return true;
            const q = searchQ.toLowerCase();
            return s.name?.toLowerCase().includes(q) || s.phone_number?.includes(q);
        })
        .sort((a, b) => {
            if (sortKey === 'calls') return (b.call_stats?.total_calls || 0) - (a.call_stats?.total_calls || 0);
            if (sortKey === 'leads') return (b.leads?.total_assigned || 0) - (a.leads?.total_assigned || 0);
            if (sortKey === 'attendance') {
                const aPct = Number((a.attendance || '0/0').split('/')[0]) / Math.max(Number((a.attendance || '0/1').split('/')[1]), 1);
                const bPct = Number((b.attendance || '0/0').split('/')[0]) / Math.max(Number((b.attendance || '0/1').split('/')[1]), 1);
                return bPct - aPct;
            }
            if (sortKey === 'followups') return (b.follow_up_count || 0) - (a.follow_up_count || 0);
            return a.name?.localeCompare(b.name);
        });

    const summaryDateLabel = data?.summary
        ? `${fmtDate(data.summary.summary_start_date)} → ${fmtDate(data.summary.summary_end_date)}`
        : '';

    /* ─── Quick-select tab style helper ─── */
    const tabStyle = (key) => ({
        padding: '6px 16px',
        borderRadius: 8,
        fontSize: 13,
        fontFamily: FONT.ui,
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        transition: 'all 0.15s',
        background: activeTab === key ? COLORS.accent : 'transparent',
        color: activeTab === key ? '#fff' : COLORS.textSecondary,
    });

    const sortBtnStyle = (key) => ({
        padding: '5px 14px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        border: `1px solid ${sortKey === key ? COLORS.accent : COLORS.border}`,
        background: sortKey === key ? COLORS.accent : COLORS.card,
        color: sortKey === key ? '#fff' : COLORS.textSecondary,
        transition: 'all 0.15s',
    });

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Sales' },
                { name: 'B2C Admin' },
            ]} />

            {/* ─── Page Header ─── */}
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ fontSize: 28, fontWeight: 800, fontFamily: FONT.ui, color: COLORS.textPrimary, lineHeight: 1.2, margin: 0 }}>
                        B2C Sales Admin{' '}
                        <span style={{ fontStyle: 'italic', fontFamily: FONT.display, fontWeight: 600 }}>
                            Dashboard
                        </span>
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 6, flexWrap: 'wrap', fontFamily: FONT.ui }}>
                        <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
                            Sales floor activity for{' '}
                            <strong style={{ color: COLORS.textPrimary }}>{summaryDateLabel}</strong>
                        </span>
                        {clockedInCount > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: COLORS.accent, fontWeight: 600 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: COLORS.success }} />
                                {clockedInCount} clocked in
                            </span>
                        )}
                        {loading && (
                            <span style={{ fontSize: 12, color: COLORS.accent, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <svg style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
                                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                Loading…
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Date Tabs + Range Picker ─── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <div style={{
                    display: 'flex', gap: 2, background: '#f0efec',
                    borderRadius: 10, padding: 3,
                }}>
                    <button style={tabStyle('today')} onClick={() => handleTabChange('today')}>Today</button>
                    <button style={tabStyle('week')} onClick={() => handleTabChange('week')}>This week</button>
                    <button style={tabStyle('mtd')} onClick={() => handleTabChange('mtd')}>Month to date</button>
                    <button style={tabStyle('custom')} onClick={() => handleTabChange('custom')}>Custom</button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <DateRangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        placeholder="Date Range"
                    />
                    {(dateRange.start || dateRange.end) && (
                        <button
                            onClick={handleResetDate}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: COLORS.textSecondary, display: 'flex', alignItems: 'center',
                            }}
                            title="Reset date filter"
                        >
                            <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* ─── Summary Strip ─── */}
            {data?.summary && <SummaryStrip summary={data.summary} totalFollowUps={totalFollowUps} />}

            {/* ─── Main Content + Sidebar Layout ─── */}
            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* Left: search + staff grid */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Search + Sort bar */}
                    {staffList.length > 0 && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap',
                        }}>
                            {/* Search */}
                            <div style={{ position: 'relative', flex: 1, minWidth: 180, maxWidth: 300 }}>
                                <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: COLORS.textMuted }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                                </svg>
                                <input
                                    type="text" placeholder="Search staff by name or phone…"
                                    value={searchQ} onChange={e => setSearchQ(e.target.value)}
                                    style={{
                                        width: '100%', paddingLeft: 34, paddingRight: 12, paddingTop: 7, paddingBottom: 7,
                                        fontSize: 13, border: `1px solid ${COLORS.border}`, borderRadius: 10,
                                        background: '#fafaf8', outline: 'none',
                                    }}
                                />
                            </div>

                            {/* Sort buttons */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMuted, letterSpacing: '0.04em' }}>SORT</span>
                                {[
                                    { key: 'name', label: 'Name' },
                                    ...(showLeads ? [{ key: 'leads', label: 'Leads' }] : []),
                                    { key: 'attendance', label: 'Attendance' },
                                    { key: 'followups', label: 'Follow-ups' },
                                    { key: 'calls', label: 'Calls' },
                                ].map(opt => (
                                    <button key={opt.key} onClick={() => setSortKey(opt.key)} style={sortBtnStyle(opt.key)}>
                                        {opt.label}{sortKey === opt.key ? ' ↑' : ''}
                                    </button>
                                ))}
                            </div>

                            <span style={{ fontSize: 12, color: COLORS.textMuted, marginLeft: 'auto', fontWeight: 500 }}>
                                {filteredSorted.length} of {staffList.length} staff
                            </span>
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && filteredSorted.length === 0 && (
                        <div style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            padding: '60px 20px', color: COLORS.textMuted, textAlign: 'center',
                        }}>
                            <svg style={{ width: 48, height: 48, marginBottom: 12, opacity: 0.3 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <p style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>No staff data found</p>
                            <p style={{ fontSize: 12, marginTop: 4 }}>Try selecting a different date range</p>
                        </div>
                    )}

                    {/* Staff cards grid — 2 columns */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 16,
                        paddingBottom: 32,
                    }}>
                        {filteredSorted.map(staff => (
                            <StaffCard key={staff.id} staff={staff} showLeads={showLeads} />
                        ))}
                    </div>
                </div>

                {/* Right sidebar */}
                <div style={{ width: 280, flexShrink: 0, position: 'sticky', top: 16 }}>
                    <TodayPanel todays_sales={data?.todays_sales} staff={staffList} data={data} />
                    <LeaderboardPanel staff={staffList} />
                    <AttentionPanel staff={staffList} summary={data?.summary} />
                </div>
            </div>

            {/* Spin animation for loader */}
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </>
    );
}
